import React, { useState } from 'react';
import { AuthAction, withAuthUser } from 'next-firebase-auth';
import firebase from 'firebase/app';
import { EyeIcon, EyeOffIcon } from '@heroicons/react/solid';
import Button from 'components/Button';
import { Form, Formik } from 'formik';
import { Input } from 'components/Inputs';
import Link from 'next/link';
import { useRouter } from 'next/router';
import * as Yup from 'yup';
import { getTranslation } from 'dictionary';
import { useUserContext } from 'context/UserContext';

const REGISTER_FIELDS = {
  EMAIL: 'email',
  PASSWORD: 'password',
};

const validateSchema = (lang: 'EN' | 'ES') =>
  Yup.object().shape({
    [REGISTER_FIELDS.EMAIL]: Yup.string()
      .email(lang === 'ES' ? 'Email inválido' : 'Email must be a valid email')
      .required(lang === 'ES' ? 'Campo requerido' : 'This is required'),
    [REGISTER_FIELDS.PASSWORD]: Yup.string()
      .min(6, lang === 'ES' ? 'Mínimo 6 caracteres' : 'Min of 6 characters')
      .max(30, lang === 'ES' ? 'Máximo 30 caracteres' : 'Max 30 characters')
      .required(lang === 'ES' ? 'Campo requerido' : 'This is required'),
  });

const errorMessage = {
  'auth/email-already-in-use': {
    EN: 'Email already in use',
    ES: 'Email ya está en uso',
  },
} as const;

function Register() {
  const { language } = useUserContext();
  const googleProvider = new firebase.auth.GoogleAuthProvider();
  const facebookProvider = new firebase.auth.FacebookAuthProvider();
  const [passwordShown, setPasswordShown] = useState(false);
  const Router = useRouter();
  const [error, setError] = useState<string | null>(null);
  const validationSchema = validateSchema(language);

  const togglePassword = () => {
    setPasswordShown(!passwordShown);
  };

  const signinWithGoogle = () => {
    firebase.auth().signInWithRedirect(googleProvider);
  };

  const signinWithFacebook = () => {
    firebase.auth().signInWithRedirect(facebookProvider);
  };

  const handleSubmit = async (values: { email: string; password: string }) => {
    const { email, password } = values;
    try {
      const firebaseResponse = await firebase
        .auth()
        .createUserWithEmailAndPassword(email, password);
      const body = {
        email: email,
        firebaseUid: firebaseResponse.user?.uid,
      };
      const response = await fetch('/api/account', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (response.status === 500) {
        setError(response.statusText);
      } else {
        Router.push('/dashboard/account');
      }
    } catch (error: any) {
      setError(error.code || error.message);
    }
  };

  const PasswordIcon = () => {
    if (passwordShown)
      return (
        <EyeOffIcon
          onClick={togglePassword}
          className="h-4 w-4 text-gray-500/80 cursor-pointer z-10 "
        />
      );
    return (
      <EyeIcon
        onClick={togglePassword}
        className="h-4 w-4 text-gray-500/80 cursor-pointer z-10"
      />
    );
  };
  return (
    <>
      <section className="h-screen flex justify-center">
        <div className="container px-4 py-20 h-full">
          <div className="flex justify-center items-center content-start flex-wrap h-full g-6 text-gray-800">
            <div className="lg:w-1/3">
              <h1 className="text-2xl text-black text-center pb-4">
                {getTranslation('login', 'REGISTER', language)}
              </h1>
              <Formik
                validationSchema={validationSchema}
                initialValues={{
                  email: '',
                  password: '',
                }}
                onSubmit={handleSubmit}
              >
                {({ isSubmitting, values }) => (
                  <Form>
                    {/* <!-- Email input --> */}
                    <Input
                      type="text"
                      placeholder={getTranslation('login', 'EMAIL', language)}
                      name="email"
                    />
                    <Input
                      type={passwordShown ? 'text' : 'password'}
                      placeholder={getTranslation(
                        'login',
                        'PASSWORD',
                        language
                      )}
                      name="password"
                      icon={<PasswordIcon />}
                    />

                    {/* <!-- Submit button --> */}
                    <Button
                      ariaLabel="Register"
                      type="submit"
                      isFullWidth={true}
                      variant="signin"
                      size="signin"
                      loading={isSubmitting}
                      disabled={isSubmitting}
                    >
                      {getTranslation('login', 'CREATE_ACCOUNT', language)}
                    </Button>
                  </Form>
                )}
              </Formik>

              <h2 className="flex items-center justify-center py-8 text-gray-500 text-sm font-light space-x-2">
                <span>{getTranslation('login', 'HAVE_ACCOUNT', language)}</span>
                <Link
                  href="/login"
                  className="text-secondary hover:text-secondary-dark font-normal"
                >
                  {getTranslation('login', 'LOGIN', language)}
                </Link>
              </h2>
              {error && (
                <div className="text-red-600 leading-none text-center  text-md w-full">
                  {/* @ts-ignore */}
                  <span>{errorMessage[error]?.[language] || error}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
const MyLoader = () => (
  <div className="flex flex-col justify-center items-center  text-black h-screen md:pb-24 ">
    <span className="flex text-2xl">Simple App</span>
    <span className="flex text-xl">Loading...</span>
  </div>
);
export default withAuthUser({
  whenAuthed: AuthAction.REDIRECT_TO_APP,
  whenUnauthedBeforeInit: AuthAction.SHOW_LOADER,
  whenUnauthedAfterInit: AuthAction.RENDER,
  LoaderComponent: MyLoader,
})(Register);
